import React, { useState, useEffect } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AxiosUrl from '../AxiosUrl';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { EditorState, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import { stateToHTML } from 'draft-js-export-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import styles from './CategoryModal.module.css';

// Sanitize slug: lowercase, replace spaces/special chars with hyphens
function sanitizeSlug(value) {
    return value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

function CreateCategoryModal({ open, setOpenCategoryModal }) {
    const [category, setCategory] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [photo, setPhoto] = useState(null);
    const [imageUrl, setImageUrl] = useState('');
    const [activeTab, setActiveTab] = useState('basic');

    // Parent category (for nested categories)
    const [parentId, setParentId] = useState('');
    const [parentOptions, setParentOptions] = useState([]); // flat list of eligible parents (level < 4)

    // SEO Fields
    const [metaTitle, setMetaTitle] = useState('');
    const [metaDescription, setMetaDescription] = useState('');
    const [slug, setSlug] = useState('');
    const [slugTouched, setSlugTouched] = useState(false);
    const [focusKeywords, setFocusKeywords] = useState([]);
    const [keywordInput, setKeywordInput] = useState('');
    const [categoryDescription, setCategoryDescription] = useState('');
    const [categoryTagline, setCategoryTagline] = useState('');

    // Article Fields
    const [articleEnabled, setArticleEnabled] = useState(false);
    const [articleTitle, setArticleTitle] = useState('');
    const [articleContent, setArticleContent] = useState('');
    const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
    const [articlePosition, setArticlePosition] = useState('below_products');

    // Editor change handler
    const handleEditorChange = (newEditorState) => {
        setEditorState(newEditorState);
        const html = stateToHTML(newEditorState.getCurrentContent());
        setArticleContent(html === '<p><br></p>' ? '' : html);
    };

    // FAQ Fields
    const [faqs, setFaqs] = useState([]);

    const uid = localStorage.getItem('id');
    const [aiLoading, setAiLoading] = useState(false);
    const [showAiPrompt, setShowAiPrompt] = useState(false);
    const [aiAbout, setAiAbout] = useState('');
    const [aiProducts, setAiProducts] = useState('');

    const handleClose = () => {
        setOpenCategoryModal(false);
    };

    const startAIGenerate = () => {
        if (!category.trim()) { toast.error('Enter category name first'); return; }
        setShowAiPrompt(true);
    };

    const handleAIGenerate = async () => {
        setShowAiPrompt(false);
        setAiLoading(true);
        const token = localStorage.getItem('token');
        try {
            const res = await fetch('https://api.ecomtechbd.com/category/ai-generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', token, uid },
                body: JSON.stringify({ categoryName: category.trim(), aboutCategory: aiAbout.trim(), productTypes: aiProducts.trim() }),
            });
            if (!res.ok) throw new Error('Server error');
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();
                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const d = JSON.parse(line.slice(6));
                        if (d.type === 'complete' && d.success) {
                            const r = d.data;
                            if (r.metaTitle) setMetaTitle(r.metaTitle);
                            if (r.metaDescription) setMetaDescription(r.metaDescription);
                            if (r.categoryTagline) setCategoryTagline(r.categoryTagline);
                            if (r.categoryDescription) setCategoryDescription(r.categoryDescription);
                            if (r.focusKeywords?.length) setFocusKeywords(r.focusKeywords);
                            if (r.faqs?.length) setFaqs(r.faqs.map((f, i) => ({ ...f, gid: String(Date.now() + i) })));
                            if (r.articleTitle) { setArticleTitle(r.articleTitle); setArticleEnabled(true); }
                            if (r.articleContent) {
                                setArticleContent(r.articleContent);
                                setArticleEnabled(true);
                                const blocks = htmlToDraft(r.articleContent);
                                if (blocks.contentBlocks) {
                                    const cs = ContentState.createFromBlockArray(blocks.contentBlocks, blocks.entityMap);
                                    setEditorState(EditorState.createWithContent(cs));
                                }
                            }
                            toast.success(`AI content generated!`);
                        } else if (d.type === 'complete' && !d.success) {
                            toast.error(d.message || 'Generation failed');
                        }
                    } catch {}
                }
            }
        } catch (err) {
            toast.error('Failed: ' + err.message);
        } finally {
            setAiLoading(false);
        }
    };

    // Load parent category options on mount
    useEffect(() => {
        AxiosUrl.get(`/category-tree/${uid}`)
            .then((res) => {
                if (res.data.success) {
                    // Flatten tree for dropdown: only allow parents at level < 4
                    const flatten = (nodes, depth = 0) => {
                        const result = [];
                        for (const node of nodes) {
                            if ((node.level || 1) < 4) {
                                result.push({ ...node, _depth: depth });
                                if (node.children && node.children.length > 0) {
                                    result.push(...flatten(node.children, depth + 1));
                                }
                            }
                        }
                        return result;
                    };
                    setParentOptions(flatten(res.data.data || []));
                }
            })
            .catch(() => {});
    }, [uid]);

    // Auto-generate slug from category name (until user manually edits slug)
    useEffect(() => {
        if (category && !slugTouched) {
            setSlug(sanitizeSlug(category));
        }
    }, [category, slugTouched]);

    // Handle keyword chip add
    const handleAddKeyword = (event) => {
        if (event.key === 'Enter' && keywordInput.trim()) {
            event.preventDefault();
            if (!focusKeywords.includes(keywordInput.trim())) {
                setFocusKeywords([...focusKeywords, keywordInput.trim()]);
            }
            setKeywordInput('');
        }
    };

    // Handle keyword chip delete
    const handleDeleteKeyword = (keywordToDelete) => {
        setFocusKeywords(focusKeywords.filter((keyword) => keyword !== keywordToDelete));
    };

    // FAQ handlers
    const addFaq = () => {
        setFaqs([...faqs, { question: '', answer: '', gid: Date.now().toString() }]);
    };

    const removeFaq = (index) => {
        setFaqs(faqs.filter((_, i) => i !== index));
    };

    const updateFaq = (index, field, value) => {
        const updated = [...faqs];
        updated[index] = { ...updated[index], [field]: value };
        setFaqs(updated);
    };

    const submitCategory = () => {
        setLoading(true);
        if (!category) {
            toast.error('Category name is required');
            setLoading(false);
            return;
        }
        if (!imageUrl) {
            toast.error('Category image is required');
            setLoading(false);
            return;
        }

        const formData = {
            uid,
            categoryName: category,
            imgUrl: imageUrl,
            message,
            parentId: parentId || null,
            // SEO Fields
            metaTitle: metaTitle || category,
            metaDescription,
            slug: slug || sanitizeSlug(category),
            focusKeywords,
            categoryDescription,
            categoryTagline,
            // Article
            categoryArticle: {
                enabled: articleEnabled,
                title: articleTitle,
                content: articleContent,
                position: articlePosition,
            },
            // FAQs
            faqs: faqs.filter(f => f.question.trim() && f.answer.trim()),
        };

        AxiosUrl.post(`/create-category`, formData)
            .then((data) => {
                if (data.data === 'Category Already Exist') {
                    toast.error(data.data);
                } else if (data.data === 'Category Successful') {
                    toast.success('Category created successfully');
                    setOpenCategoryModal(false);
                }
                setLoading(false);
                setCategory('');
                setMessage('');
                setImageUrl('');
                setPhoto(null);
                setMetaTitle('');
                setMetaDescription('');
                setSlug('');
                setSlugTouched(false);
                setFocusKeywords([]);
                setCategoryDescription('');
                setCategoryTagline('');
                setArticleEnabled(false);
                setArticleTitle('');
                setArticleContent('');
                setEditorState(EditorState.createEmpty());
                setArticlePosition('below_products');
                setFaqs([]);
            })
            .catch((error) => {
                console.log(error);
                toast.error('Failed to create category');
                setLoading(false);
            });
    };

    const cdnImage = async () => {
        const image = new FormData();
        image.append('photo', photo);
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data',
                token: 'zesrxdyctuutydrszrestdrxy',
            },
        };
        const url = 'https://cdn.ecomtechbd.com/upload/category';
        axios
            .post(url, image, config)
            .then((res) => {
                toast.success('Image uploaded to CDN');
                setImageUrl(res.data);
            })
            .catch((error) => {
                console.log(error);
                toast.error('Failed to upload image');
            });
    };

    useEffect(() => {
        if (photo) {
            cdnImage();
        }
    }, [photo]);

    const handleFileChange = (event) => {
        setPhoto(event.target.files[0]);
    };

    // Render tab content
    const renderBasicTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.imageUploadSection}>
                {!imageUrl ? (
                    <label className={styles.uploadButton}>
                        <input
                            hidden
                            accept="image/*"
                            type="file"
                            onChange={handleFileChange}
                        />
                        <CloudUploadIcon />
                        <span>Upload Image</span>
                    </label>
                ) : (
                    <div className={styles.imagePreviewContainer}>
                        <img
                            src={imageUrl}
                            alt="Category"
                            className={styles.imagePreview}
                        />
                        <label className={styles.reuploadOverlay}>
                            <input
                                hidden
                                accept="image/*"
                                type="file"
                                onChange={handleFileChange}
                            />
                            <CloudUploadIcon />
                            <span>Re-upload</span>
                        </label>
                    </div>
                )}
            </div>

            <div className={styles.formGroup}>
                <label>Category Name *</label>
                <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="Enter category name"
                />
            </div>

            <div className={styles.formGroup}>
                <label>Category Message (Optional)</label>
                <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Optional message for this category"
                />
            </div>
        </div>
    );

    const renderSEOTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>SEO Title</label>
                <input
                    type="text"
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                    placeholder={category || 'Auto-generated from category name'}
                />
                <small className={`${styles.characterCounter} ${metaTitle.length > 60 ? styles.warning : ''}`}>
                    {metaTitle.length}/60 characters
                </small>
            </div>

            <div className={styles.formGroup}>
                <label>Meta Description</label>
                <textarea
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    placeholder="Brief description for search engines"
                    rows={3}
                />
                <small className={`${styles.characterCounter} ${metaDescription.length > 160 ? styles.warning : ''}`}>
                    {metaDescription.length}/160 characters
                </small>
            </div>

            <div className={styles.formGroup}>
                <label>URL Slug</label>
                <input
                    type="text"
                    value={slug}
                    onChange={(e) => { setSlugTouched(true); setSlug(sanitizeSlug(e.target.value)); }}
                    placeholder="auto-generated-slug"
                />
                <small>Auto-generated from category name, edit if needed</small>
            </div>

            <div className={styles.formGroup}>
                <label>Focus Keywords</label>
                <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={handleAddKeyword}
                    placeholder="Type a keyword and press Enter"
                />
                <small>Press Enter to add each keyword</small>
            </div>

            {focusKeywords.length > 0 && (
                <div className={styles.keywordChipsContainer}>
                    {focusKeywords.map((keyword) => (
                        <div key={keyword} className={styles.keywordChip}>
                            <span>{keyword}</span>
                            <button onClick={() => handleDeleteKeyword(keyword)}>×</button>
                        </div>
                    ))}
                </div>
            )}

            <div className={styles.formGroup}>
                <label>Category Tagline</label>
                <input
                    type="text"
                    value={categoryTagline}
                    onChange={(e) => setCategoryTagline(e.target.value)}
                    placeholder="Short promotional tagline for the category"
                />
                <small>Displayed as a header on the category page</small>
            </div>

            <div className={styles.formGroup}>
                <label>Category Description</label>
                <textarea
                    value={categoryDescription}
                    onChange={(e) => setCategoryDescription(e.target.value)}
                    placeholder="Long-form description for category page (300-500 words recommended)"
                    rows={6}
                />
                <small>For SEO and content purposes</small>
            </div>
        </div>
    );

    const renderArticleTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.sectionInfo}>
                Add an SEO article to the category page. This helps improve search rankings with rich, keyword-optimized content.
            </div>

            <div className={styles.toggleRow}>
                <label>Enable Article</label>
                <button
                    type="button"
                    className={`${styles.toggleSwitch} ${articleEnabled ? styles.active : ''}`}
                    onClick={() => setArticleEnabled(!articleEnabled)}
                />
            </div>

            {articleEnabled && (
                <>
                    <div className={styles.formGroup}>
                        <label>Article Title</label>
                        <input
                            type="text"
                            value={articleTitle}
                            onChange={(e) => setArticleTitle(e.target.value)}
                            placeholder="e.g., About Our Skincare Collection"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Article Content</label>
                        <div className={styles.editorWrapper}>
                            <Editor
                                editorState={editorState}
                                onEditorStateChange={handleEditorChange}
                                wrapperClassName={styles.editorWrapperInner}
                                editorClassName={styles.editorContent}
                                toolbarClassName={styles.editorToolbar}
                                placeholder="Write your article content here..."
                                toolbar={{
                                    options: ['inline', 'blockType', 'list', 'textAlign', 'link', 'history'],
                                    inline: { inDropdown: false, options: ['bold', 'italic', 'underline', 'strikethrough'] },
                                    blockType: { inDropdown: true, options: ['Normal', 'H2', 'H3', 'H4', 'Blockquote'] },
                                    list: { inDropdown: false, options: ['unordered', 'ordered'] },
                                    textAlign: { inDropdown: true },
                                    link: { inDropdown: false, showOpenOptionOnHover: true, defaultTargetOption: '_blank' },
                                    history: { inDropdown: false, options: ['undo', 'redo'] },
                                }}
                            />
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label>Article Position</label>
                        <select
                            value={articlePosition}
                            onChange={(e) => setArticlePosition(e.target.value)}
                        >
                            <option value="below_products">Below Products (Default)</option>
                            <option value="above_products">Above Products</option>
                        </select>
                    </div>
                </>
            )}
        </div>
    );

    const renderFAQsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.sectionInfo}>
                Add FAQs to generate FAQ Schema markup (structured data) for better SEO. These appear as an accordion on the category page.
            </div>

            {faqs.length === 0 && (
                <div className={styles.emptyFaqMessage}>
                    No FAQs added yet. Click the button below to add your first FAQ.
                </div>
            )}

            {faqs.map((faq, index) => (
                <div key={faq.gid} className={styles.faqItem}>
                    <div className={styles.faqItemHeader}>
                        <span>FAQ #{index + 1}</span>
                        <button
                            type="button"
                            className={styles.removeFaqBtn}
                            onClick={() => removeFaq(index)}
                        >
                            ×
                        </button>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Question</label>
                        <input
                            type="text"
                            value={faq.question}
                            onChange={(e) => updateFaq(index, 'question', e.target.value)}
                            placeholder="Enter the FAQ question"
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Answer</label>
                        <textarea
                            value={faq.answer}
                            onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                            placeholder="Enter the answer"
                            rows={3}
                        />
                    </div>
                </div>
            ))}

            <button type="button" className={styles.addFaqBtn} onClick={addFaq}>
                + Add FAQ
            </button>
        </div>
    );

    if (!open) return null;

    return (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <ToastContainer />
            <div className={styles.modal}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <h2>Create New Category</h2>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button type="button" onClick={startAIGenerate} disabled={aiLoading}
                            style={{ padding: '6px 14px', background: aiLoading ? '#e8e8ee' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: aiLoading ? '#6d7175' : '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: aiLoading ? 'not-allowed' : 'pointer' }}>
                            {aiLoading ? 'Generating...' : 'AI Generate'}
                        </button>
                        <button className={styles.closeButton} onClick={handleClose}>×</button>
                    </div>
                </div>
                {showAiPrompt && (
                    <div style={{ padding: '16px 24px', background: '#f0f0ff', borderBottom: '1px solid #e1e3e5' }}>
                        <p style={{ fontSize: '13px', fontWeight: 600, color: '#202223', marginBottom: '10px' }}>Tell AI about this category:</p>
                        <input type="text" placeholder="What is this category about? e.g. Premium skincare and beauty products" value={aiAbout} onChange={e => setAiAbout(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box' }} />
                        <input type="text" placeholder="What products are in this category? e.g. Face wash, serums, moisturizers" value={aiProducts} onChange={e => setAiProducts(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '13px', marginBottom: '10px', boxSizing: 'border-box' }} />
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowAiPrompt(false)} style={{ padding: '6px 14px', background: '#fff', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={handleAIGenerate} style={{ padding: '6px 14px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Generate</button>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className={styles.tabs}>
                    <button
                        className={activeTab === 'basic' ? styles.activeTab : ''}
                        onClick={() => setActiveTab('basic')}
                    >
                        Basic Info
                    </button>
                    <button
                        className={activeTab === 'seo' ? styles.activeTab : ''}
                        onClick={() => setActiveTab('seo')}
                    >
                        SEO Settings
                    </button>
                    <button
                        className={activeTab === 'article' ? styles.activeTab : ''}
                        onClick={() => setActiveTab('article')}
                    >
                        Article
                    </button>
                    <button
                        className={activeTab === 'faqs' ? styles.activeTab : ''}
                        onClick={() => setActiveTab('faqs')}
                    >
                        FAQs
                    </button>
                </div>

                {/* Tab Content */}
                {activeTab === 'basic' && renderBasicTab()}
                {activeTab === 'seo' && renderSEOTab()}
                {activeTab === 'article' && renderArticleTab()}
                {activeTab === 'faqs' && renderFAQsTab()}

                {/* Modal Footer */}
                <div className={styles.modalFooter}>
                    <button className={styles.cancelButton} onClick={handleClose}>
                        Cancel
                    </button>
                    <button
                        className={styles.submitButton}
                        onClick={submitCategory}
                        disabled={loading}
                    >
                        {loading ? 'Creating...' : 'Create Category'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CreateCategoryModal;
